import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getTrip, updateTrip, deleteTrip, addStop, deleteStop, addEvent, deleteEvent, addBooking, deleteBooking, addReference, tripDurationDays } from '../lib/api';
import type { TripWithChildren, TripStatus } from '../lib/types';
import { WatercolorBloom } from '../components/WatercolorBloom';
import { IconChevronLeft, IconPlus, IconLightning, IconDroplet, IconStar, IconGlobe, IconChat } from '../components/Icons';

const STATUS_OPTIONS: TripStatus[] = ['idea','researching','planning','booked','in_progress','completed','archived'];

export default function TripDetail() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const [trip, setTrip] = useState<TripWithChildren | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function reload() {
    try { setTrip(await getTrip(id)); } catch (e: any) { setErr(e?.message ?? 'load failed'); }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  async function onStatus(s: TripStatus) {
    if (!trip) return;
    await updateTrip(trip.id, { status: s });
    reload();
  }
  async function onDelete() {
    if (!trip) return;
    if (!confirm(`Delete "${trip.title}"? This removes all stops, bookings, events, refs.`)) return;
    await deleteTrip(trip.id);
    nav('/');
  }

  if (err) return <div className="card" style={{ borderColor: '#FF2BD6' }}><span className="label-bright">Error</span><br/>{err}</div>;
  if (!trip) return <div className="card" style={{ padding: 48, textAlign: 'center' }}><span className="label">// Loading…</span></div>;

  const days = tripDurationDays(trip);

  return (
    <div className="col" style={{ gap: 24 }}>
      <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--c-uv)', fontFamily: 'var(--font-ui)', textTransform: 'uppercase', fontSize: 12, letterSpacing: '0.06em' }}>
        <IconChevronLeft size={16} /> All trips
      </Link>

      <section className="card card-dark brackets" style={{ position: 'relative', overflow: 'hidden', padding: 32 }}>
        <WatercolorBloom variant="duo" size={500} opacity={0.45} style={{ position: 'absolute', top: -160, right: -120 }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <span className="label" style={{ color: 'var(--c-cyan)' }}>// TRIP RECORD</span>
          <h1 className="h1" style={{ marginTop: 8, color: 'white' }}>{trip.title}</h1>
          {trip.summary && <p className="body" style={{ color: 'var(--c-lavender)', maxWidth: 720, marginTop: 12 }}>{trip.summary}</p>}

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 20 }}>
            <Detail label="Status">
              <select className="select" value={trip.status} onChange={e => onStatus(e.target.value as TripStatus)} style={{ width: 'auto' }}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_',' ').toUpperCase()}</option>)}
              </select>
            </Detail>
            <Detail label="Dates"><span style={{ color: 'white' }}>{formatRange(trip.start_date, trip.end_date)}</span></Detail>
            {days !== null && <Detail label="Length"><span style={{ color: 'white' }}>{days} days</span></Detail>}
            <Detail label="Stops"><span style={{ color: 'white' }}>{trip.stops.length}</span></Detail>
            <Detail label="Bookings"><span style={{ color: 'white' }}>{trip.bookings.length}</span></Detail>
            <Detail label="Events"><span style={{ color: 'white' }}>{trip.events.length}</span></Detail>
          </div>
        </div>
      </section>

      <Section title="Stops" count={trip.stops.length} action={<AddStopButton tripId={trip.id} onAdded={reload} />}>
        {trip.stops.length === 0 ? <Empty label="No stops yet. Add a destination to begin." />
          : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px,1fr))', gap: 16 }}>
              {trip.stops.map(s => (
                <article key={s.id} className="card" style={{ position: 'relative' }}>
                  <span className="label-bright">// STOP {s.sort_order + 1}</span>
                  <h3 className="h3" style={{ marginTop: 6 }}>{s.city}{s.country ? `, ${s.country}` : ''}</h3>
                  {s.purpose && <p className="body" style={{ marginTop: 6 }}>{s.purpose}</p>}
                  <div className="label muted" style={{ marginTop: 8 }}>{formatRange(s.start_date, s.end_date)}</div>
                  <button className="btn btn-ghost" style={{ marginTop: 12, padding: '6px 12px', fontSize: 11 }}
                          onClick={async () => { if (confirm(`Remove stop ${s.city}?`)) { await deleteStop(s.id); reload(); } }}>Remove</button>
                </article>
              ))}
            </div>}
      </Section>

      <Section title="Bookings" count={trip.bookings.length} action={<AddBookingButton tripId={trip.id} onAdded={reload} />}>
        {trip.bookings.length === 0 ? <Empty label="No flights, hotels, or transfers yet." />
          : <div className="col" style={{ gap: 8 }}>
              {trip.bookings.map(b => (
                <article key={b.id} className="card row-between" style={{ alignItems: 'flex-start' }}>
                  <div className="row" style={{ alignItems: 'flex-start', gap: 14 }}>
                    <span style={{ marginTop: 2 }}>
                      {b.booking_type === 'flight' ? <IconLightning color="var(--c-magenta)" />
                       : b.booking_type === 'hotel' ? <IconDroplet color="var(--c-cyan)" />
                       : <IconStar color="var(--c-uv)" />}
                    </span>
                    <div>
                      <div className="row" style={{ gap: 10 }}>
                        <span className="h3">{bookingHeadline(b)}</span>
                        <span className={`pill pill-${b.status === 'confirmed' ? 'booked' : b.status === 'tentative' ? 'planning' : 'idea'}`}>{b.status}</span>
                        {b.source && b.source !== 'manual' && <span className="pill pill-completed">{b.source}</span>}
                      </div>
                      <div className="body muted" style={{ marginTop: 6 }}>{bookingDetails(b)}</div>
                      {b.confirmation && <div className="label muted" style={{ marginTop: 4 }}>Conf {b.confirmation}</div>}
                    </div>
                  </div>
                  <button className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: 11 }}
                          onClick={async () => { if (confirm('Remove booking?')) { await deleteBooking(b.id); reload(); } }}>Remove</button>
                </article>
              ))}
            </div>}
      </Section>

      <Section title="Events" count={trip.events.length} action={<AddEventButton tripId={trip.id} onAdded={reload} />}>
        {trip.events.length === 0 ? <Empty label="Drop links or add events to start filling this trip." />
          : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 16 }}>
              {trip.events.map(ev => (
                <article key={ev.id} className="card brackets" style={{ position: 'relative' }}>
                  <WatercolorBloom variant="lime" size={220} opacity={0.3} style={{ position: 'absolute', top: -60, right: -50 }} />
                  <div style={{ position: 'relative' }}>
                    <span className="label-bright">// {ev.event_type.toUpperCase()}</span>
                    <h3 className="h3" style={{ marginTop: 6 }}>{ev.title}</h3>
                    {ev.location_name && <div className="label muted" style={{ marginTop: 4 }}>{ev.location_name}</div>}
                    {ev.start_at && <div className="label" style={{ marginTop: 4 }}>{new Date(ev.start_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</div>}
                    {ev.description && <p className="body muted" style={{ marginTop: 8 }}>{ev.description}</p>}
                    {ev.source_url && <a href={ev.source_url} target="_blank" rel="noreferrer" className="label-bright" style={{ display: 'inline-block', marginTop: 8 }}>Source link →</a>}
                    <div style={{ marginTop: 10 }}>
                      <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 10 }} onClick={async () => { if (confirm('Remove event?')) { await deleteEvent(ev.id); reload(); } }}>Remove</button>
                    </div>
                  </div>
                </article>
              ))}
            </div>}
      </Section>

      <Section title="References" count={trip.references.length} action={<AddReferenceButton tripId={trip.id} onAdded={reload} />}>
        {trip.references.length === 0 ? <Empty label="No saved links or notes yet." />
          : <div className="col" style={{ gap: 8 }}>
              {trip.references.map(r => (
                <a key={r.id} href={r.url ?? '#'} target="_blank" rel="noreferrer" className="card row" style={{ color: 'inherit', textDecoration: 'none' }}>
                  <IconChat color="var(--c-uv)" />
                  <div>
                    <div className="h3">{r.title ?? r.url}</div>
                    {r.excerpt && <div className="body muted">{r.excerpt}</div>}
                  </div>
                </a>
              ))}
            </div>}
      </Section>

      <Section title="Notes">
        <NotesEditor trip={trip} onSaved={reload} />
      </Section>

      <div style={{ marginTop: 32 }}>
        <button className="btn btn-ghost" onClick={onDelete} style={{ color: '#FF2BD6', borderColor: 'rgba(255,43,214,0.4)' }}>Delete trip</button>
      </div>
    </div>
  );
}

function Section({ title, count, action, children }: any) {
  return (
    <section>
      <div className="row-between" style={{ marginBottom: 12 }}>
        <h2 className="h3"># {title}{count !== undefined && <span className="label muted" style={{ marginLeft: 8 }}>({count})</span>}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Detail({ label, children }: { label: string, children: React.ReactNode }) {
  return (
    <div className="col" style={{ gap: 4 }}>
      <span className="label" style={{ color: 'rgba(232,217,255,0.7)' }}>{label}</span>
      <div>{children}</div>
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return <div className="card" style={{ padding: 24, textAlign: 'center' }}><span className="label muted">{label}</span></div>;
}

function formatRange(s?: string | null, e?: string | null): string {
  if (!s && !e) return '—';
  if (s && e) return `${fmt(s)} → ${fmt(e)}`;
  return fmt((s ?? e) as string);
}
function fmt(d: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (!m) return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function bookingHeadline(b: any): string {
  if (b.booking_type === 'flight') {
    const fn = b.flight_number ?? '';
    const seg = [b.origin_code ?? b.origin_city, b.dest_code ?? b.dest_city].filter(Boolean).join(' → ');
    return `${fn} ${seg}`.trim();
  }
  if (b.booking_type === 'hotel') return b.property_name ?? b.vendor ?? 'Hotel';
  return b.vendor ?? b.booking_type;
}

function bookingDetails(b: any): string {
  const parts: string[] = [];
  if (b.vendor && b.booking_type === 'flight') parts.push(b.vendor);
  if (b.start_at) parts.push(new Date(b.start_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }));
  if (b.end_at)   parts.push(`→ ${new Date(b.end_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`);
  if (b.room_type) parts.push(b.room_type);
  return parts.join(' · ');
}

function AddStopButton({ tripId, onAdded }: { tripId: string, onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [purpose, setPurpose] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  if (!open) return <button className="btn btn-secondary" onClick={() => setOpen(true)}><IconPlus size={14} /> Stop</button>;
  return (
    <form className="card" style={{ width: 360 }} onSubmit={async e => {
      e.preventDefault();
      await addStop({ trip_id: tripId, city, country: country || null, purpose: purpose || null, start_date: start || null, end_date: end || null });
      setOpen(false); onAdded();
    }}>
      <span className="label-bright">// New stop</span>
      <div className="col" style={{ marginTop: 10 }}>
        <input className="input" placeholder="City" required value={city} onChange={e => setCity(e.target.value)} />
        <input className="input" placeholder="Country" value={country} onChange={e => setCountry(e.target.value)} />
        <input className="input" placeholder="Purpose (visit mom, conference…)" value={purpose} onChange={e => setPurpose(e.target.value)} />
        <div className="row" style={{ gap: 8 }}>
          <input className="input" type="date" value={start} onChange={e => setStart(e.target.value)} />
          <input className="input" type="date" value={end} onChange={e => setEnd(e.target.value)} />
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button type="submit" className="btn btn-primary">Add</button>
          <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
        </div>
      </div>
    </form>
  );
}

function AddBookingButton({ tripId, onAdded }: { tripId: string, onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<'flight'|'hotel'|'car'|'train'|'transfer'|'other'>('flight');
  const [vendor, setVendor] = useState('');
  const [conf, setConf] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [extra, setExtra] = useState('');
  if (!open) return <button className="btn btn-secondary" onClick={() => setOpen(true)}><IconPlus size={14} /> Booking</button>;
  return (
    <form className="card" style={{ width: 380 }} onSubmit={async e => {
      e.preventDefault();
      const payload: any = { trip_id: tripId, booking_type: type, status: 'confirmed', vendor: vendor || null, confirmation: conf || null, start_at: start || null, end_at: end || null };
      if (type === 'flight') payload.flight_number = extra || null;
      if (type === 'hotel')  payload.property_name = extra || null;
      await addBooking(payload);
      setOpen(false); onAdded();
    }}>
      <span className="label-bright">// New booking</span>
      <div className="col" style={{ marginTop: 10 }}>
        <select className="select" value={type} onChange={e => setType(e.target.value as any)}>
          <option value="flight">Flight</option>
          <option value="hotel">Hotel</option>
          <option value="car">Car</option>
          <option value="train">Train</option>
          <option value="transfer">Transfer</option>
          <option value="other">Other</option>
        </select>
        <input className="input" placeholder={type === 'flight' ? 'Airline (e.g. United)' : type === 'hotel' ? 'Brand / chain' : 'Vendor'} value={vendor} onChange={e => setVendor(e.target.value)} />
        <input className="input" placeholder={type === 'flight' ? 'Flight # (e.g. UA 1470)' : type === 'hotel' ? 'Property name' : 'Detail'} value={extra} onChange={e => setExtra(e.target.value)} />
        <input className="input" placeholder="Confirmation #" value={conf} onChange={e => setConf(e.target.value)} />
        <div className="row" style={{ gap: 8 }}>
          <input className="input" type="datetime-local" value={start} onChange={e => setStart(e.target.value)} />
          <input className="input" type="datetime-local" value={end} onChange={e => setEnd(e.target.value)} />
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button type="submit" className="btn btn-primary">Add</button>
          <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
        </div>
      </div>
    </form>
  );
}

function AddEventButton({ tripId, onAdded }: { tripId: string, onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [loc, setLoc] = useState('');
  const [start, setStart] = useState('');
  if (!open) return <button className="btn btn-secondary" onClick={() => setOpen(true)}><IconPlus size={14} /> Event</button>;
  return (
    <form className="card" style={{ width: 360 }} onSubmit={async e => {
      e.preventDefault();
      await addEvent({ trip_id: tripId, title, description: desc || null, location_name: loc || null, start_at: start || null });
      setOpen(false); onAdded();
    }}>
      <span className="label-bright">// New event</span>
      <div className="col" style={{ marginTop: 10 }}>
        <input className="input" required placeholder="Title (e.g. GLC 2027)" value={title} onChange={e => setTitle(e.target.value)} />
        <input className="input" placeholder="Location" value={loc} onChange={e => setLoc(e.target.value)} />
        <input className="input" type="datetime-local" value={start} onChange={e => setStart(e.target.value)} />
        <textarea className="textarea" placeholder="Notes" value={desc} onChange={e => setDesc(e.target.value)} />
        <div className="row" style={{ gap: 8 }}>
          <button type="submit" className="btn btn-primary">Add</button>
          <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
        </div>
      </div>
    </form>
  );
}

function AddReferenceButton({ tripId, onAdded }: { tripId: string, onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  if (!open) return <button className="btn btn-secondary" onClick={() => setOpen(true)}><IconPlus size={14} /> Link</button>;
  return (
    <form className="card" style={{ width: 360 }} onSubmit={async e => {
      e.preventDefault();
      await addReference({ trip_id: tripId, url, title: title || url });
      setOpen(false); onAdded();
    }}>
      <span className="label-bright">// New reference</span>
      <div className="col" style={{ marginTop: 10 }}>
        <input className="input" required placeholder="URL" value={url} onChange={e => setUrl(e.target.value)} />
        <input className="input" placeholder="Title (optional)" value={title} onChange={e => setTitle(e.target.value)} />
        <div className="row" style={{ gap: 8 }}>
          <button type="submit" className="btn btn-primary">Save</button>
          <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
        </div>
      </div>
    </form>
  );
}

function NotesEditor({ trip, onSaved }: any) {
  const [notes, setNotes] = useState(trip.notes ?? '');
  const [saving, setSaving] = useState(false);
  return (
    <div className="card">
      <textarea className="textarea" style={{ minHeight: 120 }} placeholder="Free-form notes…" value={notes} onChange={e => setNotes(e.target.value)} />
      <div className="row" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary" disabled={saving} onClick={async () => {
          setSaving(true);
          await updateTrip(trip.id, { notes });
          setSaving(false); onSaved();
        }}>{saving ? 'Saving…' : 'Save notes'}</button>
      </div>
    </div>
  );
}
